import React, { useState } from 'react';
import { Download, Eye, FileText, Users, X, Send, CheckCircle, Clock, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../components/ui/sonner';
import * as api from '../../services/api';

const AccountsPage = ({ client, refreshClient, onNavigateToTab }) => {
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [downloadingOffer, setDownloadingOffer] = useState(false);

  // Use accounts from client data
  const accounts = client.accounts && client.accounts.length > 0 
    ? client.accounts 
    : [
        { type: 'apport', balance: client.contribution || 0, status: 'pending', currency: 'EUR' },
        { type: 'credit', balance: client.loanAmount || 0, status: 'blocked', currency: 'EUR' }
      ];

  const apportAccount = accounts.find(a => a.type === 'apport') || { balance: client.contribution || 0, status: 'pending' };
  const creditAccount = accounts.find(a => a.type === 'credit') || { balance: client.loanAmount || 0, status: 'blocked' };

  // Calculate monthly payment
  const calculateMonthlyPayment = () => {
    const principal = client.loanAmount || 0;
    const annualRate = client.interestRate || 3.5; // Default 3.5% if not set
    const years = client.loanDuration || 20;
    
    if (principal === 0 || years === 0) return 0;
    
    const monthlyRate = annualRate / 100 / 12;
    const numberOfPayments = years * 12;
    
    if (monthlyRate === 0) {
      return principal / numberOfPayments;
    }
    
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    return monthlyPayment;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Download offer PDF from API
  const handleDownloadOffer = async () => {
    try {
      setDownloadingOffer(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const downloadUrl = `${API_URL}/api/clients/${client.id}/offer-pdf/download`;
      window.open(downloadUrl, '_blank');
      toast.success('Téléchargement en cours...');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingOffer(false);
    }
  };

  const handleConsultDocuments = () => {
    setShowDocumentsModal(true);
  };

  const handleContactAdvisor = () => {
    setShowMessageModal(true);
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    try {
      setSendingMessage(true);
      await api.sendMessage(client.id, {
        type: 'message',
        subject: messageSubject,
        content: messageContent
      });
      toast.success('Message envoyé à votre conseiller');
      setShowMessageModal(false);
      setMessageSubject('');
      setMessageContent('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Build list of all documents including offer PDF
  const getAllDocuments = () => {
    const docs = [];
    if (client.offerPdfUrl) {
      docs.push({
        id: 'offer-pdf',
        name: client.offerPdfFileName || 'Offre Fortuneo',
        type: 'offer',
        uploadDate: 'Document officiel',
        hasFile: true
      });
    }
    if (client.documents && client.documents.length > 0) {
      docs.push(...client.documents);
    }
    return docs;
  };

  const handleDownloadDocument = async (doc) => {
    try {
      if (doc.id === 'offer-pdf') {
        await handleDownloadOffer();
      } else {
        const data = await api.downloadFile(client.id, doc.id);
        if (data.fileData) {
          const fileName = data.fileName || doc.name;
          const base64Data = data.fileData.split(',')[1] || data.fileData;
          const mimeType = data.fileType || 'application/octet-stream';
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
          toast.success('Document téléchargé');
        }
      }
    } catch (error) {
      toast.error('Erreur de téléchargement');
    }
  };

  const allDocuments = getAllDocuments();
  // Use manual monthlyPayment from admin if set, otherwise calculate
  const monthlyPayment = client.monthlyPayment || calculateMonthlyPayment();

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white border-0">
        <CardContent className="p-6">
          <h2 className="text-2xl font-bold mb-2">Bonjour {client.firstName} 👋</h2>
          <p className="text-green-100">Bienvenue dans votre espace Fortuneo Immobilier</p>
        </CardContent>
      </Card>

      {/* Apport Personnel Card */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">💰 Votre Apport</p>
              <CardTitle className="text-lg">Apport Personnel</CardTitle>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              apportAccount.status === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {apportAccount.status === 'active' ? 'Confirmé' : 'En attente'}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Always show the actual contribution amount */}
          <div className="mb-4">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(client.contribution || 0)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Montant de votre apport personnel</p>
          </div>

          {/* Display received amount if any */}
          {(client.receivedAmount > 0) && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Versement reçu</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(client.receivedAmount)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Reste à verser</p>
                  <p className="text-lg font-semibold text-gray-700">
                    {formatCurrency((client.contribution || 0) - (client.receivedAmount || 0))}
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(100, ((client.receivedAmount || 0) / (client.contribution || 1)) * 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {Math.round(((client.receivedAmount || 0) / (client.contribution || 1)) * 100)}% versé
                </p>
              </div>
            </div>
          )}

          {/* Status message based on apport status */}
          {apportAccount.status === 'active' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-semibold text-green-800">Versement confirmé</p>
                  <p className="text-sm text-green-700 mt-1">
                    Votre apport de {formatCurrency(client.contribution || 0)} a bien été enregistré.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Clock className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-semibold text-red-800">Versement en attente de réception</p>
                  <p className="text-sm text-red-700 mt-1">
                    Votre apport sera crédité sur votre compte une fois le versement reçu et validé par votre conseiller.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compte Crédit Card */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">🏠 Votre Crédit</p>
              <CardTitle className="text-lg">Compte Crédit Immobilier</CardTitle>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              creditAccount.status === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {creditAccount.status === 'active' ? 'Débloqué' : 'En attente'}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(creditAccount.balance || client.loanAmount || 0)}</p>
            <p className="text-sm text-gray-500 mt-1">
              {creditAccount.status === 'active' ? 'Montant disponible' : 'Montant du prêt demandé'}
            </p>
          </div>

          {creditAccount.status === 'active' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-semibold text-green-800">Crédit débloqué</p>
                  <p className="text-sm text-green-700 mt-1">
                    Votre crédit de {formatCurrency(creditAccount.balance || client.loanAmount || 0)} est disponible.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm font-semibold text-red-800">En attente de déblocage</p>
                  <p className="text-sm text-red-700 mt-1">
                    Ce montant sera débloqué après validation complète de votre dossier.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Détails du Prêt Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="text-green-600" size={20} />
            Détails de votre prêt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Montant demandé</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(client.loanAmount || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Durée</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{client.loanDuration || 0} ans</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">T.A.E.G</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{(client.interestRate || 3.5).toFixed(2)} %</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-sm text-green-600 font-medium">Mensualité assurance comprise</p>
              <p className="text-xl font-bold text-green-700 mt-1">{formatCurrency(monthlyPayment)}</p>
            </div>
          </div>

          {/* État du dossier */}
          <div className={`rounded-lg p-4 ${
            client.status === 'Approuvé' 
              ? 'bg-green-50 border border-green-200' 
              : client.status === 'En cours'
                ? 'bg-green-50 border border-green-200'
                : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-start gap-3">
              {client.status === 'Approuvé' ? (
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <Clock className={`flex-shrink-0 mt-0.5 ${client.status === 'En cours' ? 'text-green-600' : 'text-yellow-600'}`} size={20} />
              )}
              <div>
                <p className={`text-sm font-semibold ${
                  client.status === 'Approuvé' 
                    ? 'text-green-800' 
                    : client.status === 'En cours'
                      ? 'text-green-800'
                      : 'text-yellow-800'
                }`}>
                  État de votre dossier : {client.status || 'En attente'}
                </p>
                <p className={`text-sm mt-1 ${
                  client.status === 'Approuvé' 
                    ? 'text-green-700' 
                    : client.status === 'En cours'
                      ? 'text-green-700'
                      : 'text-yellow-700'
                }`}>
                  {client.status === 'Approuvé' 
                    ? 'Votre dossier a été approuvé.'
                    : client.status === 'En cours'
                      ? 'Votre dossier est en cours d\'étude par nos équipes.'
                      : 'Votre dossier est en attente de traitement.'
                  }
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Contacter mon conseiller</h2>
              <Button onClick={() => setShowMessageModal(false)} variant="ghost" size="sm">
                <X size={20} />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Sujet (optionnel)</Label>
                <Input
                  value={messageSubject}
                  onChange={(e) => setMessageSubject(e.target.value)}
                  placeholder="Ex: Question sur mon dossier"
                  className="mt-1"
                  data-testid="message-subject"
                />
              </div>
              <div>
                <Label>Votre message *</Label>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Écrivez votre message ici..."
                  className="w-full border rounded-lg p-3 mt-1 min-h-[150px] resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="message-content"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleSendMessage} 
                  disabled={sendingMessage || !messageContent.trim()}
                  className="flex-1"
                  style={{ backgroundColor: '#82C43C' }}
                  data-testid="send-message-btn"
                >
                  <Send size={16} className="mr-2" />
                  {sendingMessage ? 'Envoi...' : 'Envoyer le message'}
                </Button>
                <Button variant="outline" onClick={() => setShowMessageModal(false)}>
                  Annuler
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Votre conseiller vous répondra dans les plus brefs délais.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Mes documents</h2>
              <Button onClick={() => setShowDocumentsModal(false)} variant="ghost" size="sm">
                <X size={20} />
              </Button>
            </div>
            <div className="p-6">
              {allDocuments.length > 0 ? (
                <div className="space-y-3">
                  {allDocuments.map((doc) => (
                    <div 
                      key={doc.id} 
                      className={`border rounded-lg p-4 flex justify-between items-center ${
                        doc.type === 'offer' ? 'bg-green-50 border-green-200' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${doc.type === 'offer' ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <FileText size={20} className={doc.type === 'offer' ? 'text-green-600' : 'text-gray-600'} />
                        </div>
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-sm text-gray-500">{doc.uploadDate}</p>
                          {doc.type === 'offer' && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                              Offre officielle
                            </span>
                          )}
                        </div>
                      </div>
                      {doc.hasFile && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadDocument(doc)}
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download size={16} className="mr-1" />
                          Télécharger
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-gray-500">Aucun document disponible</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Vos documents seront disponibles ici une fois ajoutés par votre conseiller.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsPage;
